/**
 * Style actions — the scene mutations styling needs.
 *
 * `adoptObject` converts an object's literal material slots into role bindings,
 * once. `applyStyleSheet` points the theme provider at a style sheet. After
 * adoption, restyling a whole scene is the second call alone: one commit, one
 * undo entry, and every bound slot in the subtree re-resolves through the
 * ancestor walk.
 *
 * Two things this deliberately does NOT do:
 *
 *  - **Bake.** Applying a style stores its URL; it does not copy the style's
 *    values into the scene. The applied style is therefore *readable* — it is
 *    the stored reference — instead of being inferred by matching values back
 *    against a library, which is the same value-equality guess that produced
 *    the identity bugs this design replaced (DESIGN_PRINCIPLES §10.8: when
 *    shapes stop separating histories, record provenance).
 *  - **Declare at adoption.** Adoption writes bindings and nothing else. There
 *    are no declarations until a sheet supplies them, and an unresolved binding
 *    does not drive — so adoption is appearance-neutral by construction rather
 *    than by keeping a set of seeds inert.
 *
 * There is no runtime "apply style" primitive either: applying is a commit,
 * which the existing path already does, and a dedicated runtime concept would
 * drift into what states already own.
 */

import type { SceneObject } from '../core/SceneObject';
import { Properties } from '../behaviors/properties';
import { Renderer } from '../behaviors/renderer';
import type {
    PropertiesData,
    PropertySheet,
} from '../scenedata/behaviors/propertiesdata';
import { PROPERTY_SHEET_VERSION } from '../scenedata/behaviors/propertiesdata';
import type { PropertyValue } from '../behaviors/tracebehavior';
import { ensureDeclaration, setDeclarationValue } from './states/statesModel';
import { declarationForValue, valueMatchesType } from './style-sheets';
import { bindRendererProperty } from './renderer-bindings';
import type { BindCommandOptions } from './renderer-bindings';
import { BehaviorCommitCommand } from './undo/BehaviorCommitCommand';
import { AddBehaviorCommand } from './undo/AddBehaviorCommand';
import { CompositeCommand } from './undo/CompositeCommand';
import { getUndoManager } from './undo/UndoManager';
import type { UndoCommand } from './undo/UndoCommand';
import { dispatchBehaviorChanged } from './undo/editor-events';
import { ROLE_SUFFIXES, rolePropertyName } from '../scenedata/material-roles';
import { surfacePropertyName } from '../scenedata/theme-vocabulary';
import type { AdoptionPlan } from './style-adoption';
import { Logger } from '../utils/Logger';
import type { ValidationError } from '../scenedata/behaviordata';

export interface StyleActionResult {
    /** Empty on success. */
    errors: ValidationError[];
    /** The undo command, or null when nothing changed / the action failed. */
    command: UndoCommand | null;
}

/** Which Renderer field each role suffix drives. */
const SUFFIX_BINDING = {
    src: 'materialSrc',
    color: 'materialColor',
    roughness: 'materialRoughness',
    metallic: 'materialMetallic',
} as const;

/**
 * The `sheets` entry that holds the applied style.
 *
 * A convention, not a wire-format field: `sheets` is an ordered list where the
 * first to declare a name supplies it, so slot 0 is simply the highest-
 * precedence sheet — the right place for the one a user swaps. Entries after it
 * are ordinary declaration sheets or layered partial styles, and they keep
 * working untouched when the style at slot 0 is replaced.
 */
export const STYLE_SHEET_SLOT = 0;

/**
 * Roll back already-executed commands after a mid-action failure, in reverse.
 *
 * Passes `silent: true` — a rollback restores state; it must not move the
 * user's selection or announce a burst of writes that net out to nothing the
 * way a user-initiated undo would.
 *
 * Returns errors instead of throwing: a failed rollback means the scene may
 * genuinely hold a partial result, and claiming atomicity anyway would be the
 * silent-residue lie §10.8 exists to prevent. Callers append the returned
 * errors so the caller of the action can see the invariant did not hold.
 * (An unwind commit that *returns* validation errors rather than throwing is
 * still invisible here — `undo()` returns void — but no behavior validates on
 * snapshot restore today; the try/catch covers the throwing paths.)
 */
async function unwindCommands(commands: UndoCommand[]): Promise<ValidationError[]> {
    try {
        for (let i = commands.length - 1; i >= 0; i--) {
            await commands[i].undo({ silent: true });
        }
        return [];
    } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        Logger.error(
            `[style-actions] Rollback failed after a failed action — the scene may hold a partial result: ${message}`,
        );
        return [{ message: `rollback failed: ${message}`, code: 'ROLLBACK_FAILED' }];
    }
}

// ── Applying a style ────────────────────────────────────────────────────────

/**
 * The style sheet URL applied at `provider`, or null when none is.
 *
 * This is the whole of "which style is active": the stored reference, read
 * back. Nothing is matched, scored, or compared against a library.
 */
export function getStyleSheet(provider: SceneObject, slot: number = STYLE_SHEET_SLOT): string | null {
    const properties = provider.getBehavior(Properties);
    return properties?.getData().sheets?.[slot] ?? null;
}

/**
 * Apply a style: reference its sheet from `provider`'s `sheets` slot.
 *
 * Replacing within the slot is what makes a swap total — the outgoing style's
 * declarations leave with its reference, so nothing of it can linger to drive a
 * property the incoming style does not set. (That is why the old design needed
 * curated libraries to be "total over families" and this one does not.)
 *
 * A provider with no Properties behavior gets one: the first style applied to a
 * scene lands on a root created with `behaviors: []`, and refusing there would
 * make the common case the broken one.
 *
 * One undo entry. Consumers re-resolve through the commit path, which reloads
 * sheets, rebuilds the property map, and notifies every subscriber in the
 * subtree.
 */
export async function applyStyleSheet(
    provider: SceneObject,
    sheetUrl: string,
    options: BindCommandOptions & { slot?: number } = {},
): Promise<StyleActionResult> {
    const slot = options.slot ?? STYLE_SHEET_SLOT;
    if (!sheetUrl) {
        return { errors: [{ message: 'A style sheet URL is required', code: 'EMPTY_SHEET_URL' }], command: null };
    }

    const existing = provider.getBehavior(Properties);
    const before: PropertiesData = existing?.getData() ?? { '@type': 'Properties', properties: [] };
    const sheets = [...(before.sheets ?? [])];
    // Beyond the end: pad rather than refuse, so a caller asking for slot 2 on
    // a provider with one sheet gets a defined layout instead of an error about
    // an array length it never chose.
    while (sheets.length < slot) sheets.push('');
    if (sheets[slot] === sheetUrl) return { errors: [], command: null };
    sheets[slot] = sheetUrl;

    return commitProviderPatch(provider, existing, before, { sheets }, `Apply style to ${provider.name}`, options);
}

/**
 * Remove the style reference at `slot`, leaving the provider unstyled.
 *
 * Trailing empty slots are dropped, and an emptied list is removed entirely, so
 * clearing a style leaves the same data a never-styled provider has rather than
 * an empty array that reads as "styled with nothing".
 */
export async function clearStyleSheet(
    provider: SceneObject,
    options: BindCommandOptions & { slot?: number } = {},
): Promise<StyleActionResult> {
    const slot = options.slot ?? STYLE_SHEET_SLOT;
    const existing = provider.getBehavior(Properties);
    if (!existing) return { errors: [], command: null };

    const before = existing.getData();
    const sheets = [...(before.sheets ?? [])];
    if (slot >= sheets.length || !sheets[slot]) return { errors: [], command: null };

    sheets[slot] = '';
    while (sheets.length > 0 && !sheets[sheets.length - 1]) sheets.pop();

    const patch: Partial<PropertiesData> = { sheets: sheets.length > 0 ? sheets : undefined };
    return commitProviderPatch(provider, existing, before, patch, `Clear style on ${provider.name}`, options);
}

/**
 * Write values as inline declarations on the provider — the theme-wide
 * override.
 *
 * Inline declarations beat every sheet, so this is how a user tweak survives
 * the style it deviates from: the style keeps supplying everything else, and
 * the overridden name resolves here. It is also the shape publish-flatten and
 * legacy value-bundle import produce.
 *
 * Declarations a value names but the provider lacks are **created**, with the
 * type its name implies (material roles) or its value implies (everything
 * else). Values whose type can't be decided, or that contradict an existing
 * declaration, are skipped with a warning rather than guessed at — one bad pair
 * must not poison the rest.
 *
 * One commit, so one undo entry, however many values.
 */
export async function setThemeValues(
    provider: SceneObject,
    values: Record<string, PropertyValue>,
    options: BindCommandOptions & { label?: string } = {},
): Promise<StyleActionResult> {
    const existing = provider.getBehavior(Properties);
    const before: PropertiesData = existing?.getData() ?? { '@type': 'Properties', properties: [] };
    let next: PropertiesData = before;

    for (const [name, value] of Object.entries(values)) {
        const template = declarationForValue(name, value);
        if (!template) {
            Logger.warn(`[setThemeValues] Skipping "${name}" — no declaration can carry this value`);
            continue;
        }
        // An existing declaration's type wins over the template's: the scene
        // already decided what this name is, and a style may not silently
        // retype it out from under the consumers bound to it.
        const declared = next.properties?.find(d => d.name === name);
        if (declared && !valueMatchesType(declared.type, value)) {
            Logger.warn(`[setThemeValues] Skipping "${name}" — value does not match the declared type "${declared.type}"`);
            continue;
        }
        next = ensureDeclaration(next, template);
        next = setDeclarationValue(next, name, value);
    }

    if (next === before) return { errors: [], command: null };
    return commitProviderPatch(
        provider, existing, before, { properties: next.properties },
        `Apply ${options.label ?? 'theme values'} to ${provider.name}`, options,
    );
}

/**
 * Serialize a provider's theme as a style sheet — "save theme as style".
 *
 * The provider's *inline* declarations are the theme: whatever a referenced
 * sheet supplies is already a style, and re-emitting it would fork a copy that
 * stops tracking the original. So this captures exactly the local overrides —
 * the deviations the user built on top of whatever style is applied — which is
 * what "save this as its own style" means.
 *
 * Returns null when there is nothing local to save.
 */
export function serializeStyleSheet(provider: SceneObject, label?: string): PropertySheet | null {
    const properties = provider.getBehavior(Properties);
    const declarations = properties?.getData().properties ?? [];
    if (declarations.length === 0) return null;
    return {
        version: PROPERTY_SHEET_VERSION,
        ...(label ? { label } : {}),
        declarations,
    };
}

/**
 * Commit a patch to the provider's Properties, attaching the behavior if it has
 * none, as one undo entry.
 *
 * Shared by every provider-side action because they share the same three
 * hazards: a bare provider needs an attach that *undoes as a removal*, a failed
 * commit must not strand the behavior that attach created, and the panel
 * inspecting the provider needs to hear about it.
 */
async function commitProviderPatch(
    provider: SceneObject,
    existing: Properties | null,
    before: PropertiesData,
    patch: Partial<PropertiesData>,
    undoLabel: string,
    options: BindCommandOptions,
): Promise<StyleActionResult> {
    const commands: UndoCommand[] = [];
    let properties = existing;
    if (!properties) {
        properties = await provider.addBehavior(Properties);
        // Add, not commit: undoing must REMOVE the behavior, not commit an
        // empty one back onto the provider.
        commands.push(new AddBehaviorCommand(provider, 'Properties', properties.getData()));
    }

    const errors = await properties.commit(patch);
    if (errors.length > 0) {
        // The attach above already mutated the scene, and this early return
        // discards the AddBehaviorCommand that would have undone it. Unwind, or
        // a failed commit leaves an orphaned empty Properties behind.
        errors.push(...await unwindCommands(commands));
        return { errors, command: null };
    }

    commands.push(new BehaviorCommitCommand(
        provider, 'Properties', before, properties.getData(), null,
    ));
    const command = commands.length === 1 ? commands[0] : new CompositeCommand(undoLabel, commands);
    if (options.record !== false && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    if (options.notify !== false) {
        dispatchBehaviorChanged({
            objectPath: provider.getPath(),
            behaviorType: 'Properties',
            action: existing ? 'updated' : 'added',
        });
    }
    return { errors: [], command };
}

// ── Adoption ────────────────────────────────────────────────────────────────

/**
 * Execute an adoption plan: bind every mesh slot it assigns to its role's
 * properties.
 *
 * That is the entire operation — no declarations, no writes to any other
 * object, no Properties behavior anywhere. The role properties the bindings
 * name need not exist: an unresolved binding does not drive, so an adopted
 * object renders exactly as it did until a style supplies values, and the
 * bindings pick them up when it does (a provider's commit fans
 * `_resubscribeAndResolve` through the subtree).
 *
 * Every write is composed into a single undo entry, and the call is **atomic**:
 * if any slot fails to bind, every bind already made is unwound before
 * returning, and all failures are reported together. Adoption never leaves a
 * partial result, because a literal left behind by a half-completed adoption is
 * indistinguishable from one the user chose over the theme, and the ownership
 * planner (`planAdoption`) reads literals as user intent. Partial adoption may
 * only ever be the outcome of a user gesture, never of this machinery.
 */
export async function adoptObject(
    sceneObject: SceneObject,
    plan: AdoptionPlan,
    options: BindCommandOptions = {},
): Promise<StyleActionResult> {
    const releases = plan.releases ?? [];
    const surfaceAssignments = plan.surfaceAssignments;
    if (plan.assignments.length === 0 && surfaceAssignments.length === 0 && releases.length === 0) {
        return { errors: [], command: null };
    }

    const commands: UndoCommand[] = [];
    const errors: ValidationError[] = [];

    // Releases first: strip the adoption-written fields from bound content
    // groups so their authored materials (the baked text/image textures) drive
    // again. Absent fields ARE the restore — the Renderer's presence gates
    // treat a missing entry as an untouched slot. One commit for all releases,
    // folded into this call's composite so the repair rides the same undo.
    if (releases.length > 0) {
        const renderer = sceneObject.getBehavior(Renderer);
        if (!renderer) {
            errors.push({ message: `No Renderer behavior on "${sceneObject.name}"`, code: 'NO_RENDERER' });
        } else {
            const releaseSlots = new Set(releases.flatMap(r => r.slotIndices));
            const before = renderer.getData();
            const materials = (before.materials ?? [])
                .map(m => {
                    if (!releaseSlots.has(m.targetMaterialIndex ?? 0)) return { ...m };
                    const { src: _src, baseColorFactor: _color, roughnessFactor: _rough, metallicFactor: _metal, ...rest } = m;
                    return rest;
                })
                // An entry left holding only its index says nothing — drop it,
                // so the released slot is byte-identical to never-adopted.
                .filter(m => Object.keys(m).some(k => k !== 'targetMaterialIndex'));
            const releaseErrors = await renderer.commit({ materials });
            if (releaseErrors.length > 0) {
                return { errors: releaseErrors, command: null };
            }
            commands.push(new BehaviorCommitCommand(sceneObject, 'Renderer', before, renderer.getData(), null));
        }
    }

    for (const assignment of plan.assignments) {
        for (const slot of assignment.slotIndices) {
            for (const suffix of ROLE_SUFFIXES) {
                const propertyName = rolePropertyName(assignment.role, suffix);
                // notify: false — one announcement for the whole adoption on
                // success (below), none if it fails and rolls back. Per-bind
                // events would describe intermediate states that may net out
                // to nothing.
                const bind = await bindRendererProperty(
                    sceneObject,
                    propertyName,
                    { binding: SUFFIX_BINDING[suffix], slot },
                    { record: false, notify: false },
                );
                if (bind.errors.length > 0) {
                    errors.push(...bind.errors.map(e => ({
                        ...e,
                        message: `${sceneObject.name} slot ${slot} → ${propertyName}: ${e.message}`,
                    })));
                    continue;
                }
                if (bind.command) commands.push(bind.command);
            }
        }
    }

    // The surface axis: colour only. A surface role has no src/roughness/
    // metallic family, so there is one property per slot rather than four —
    // otherwise this is the material loop, and a failure unwinds with it.
    for (const assignment of surfaceAssignments) {
        for (const slot of assignment.slotIndices) {
            const propertyName = surfacePropertyName(assignment.role, 'color');
            const bind = await bindRendererProperty(
                sceneObject,
                propertyName,
                { binding: 'materialColor', slot },
                { record: false, notify: false },
            );
            if (bind.errors.length > 0) {
                errors.push(...bind.errors.map(e => ({
                    ...e,
                    message: `${sceneObject.name} slot ${slot} → ${propertyName}: ${e.message}`,
                })));
                continue;
            }
            if (bind.command) commands.push(bind.command);
        }
    }

    // Atomicity: any bind failure unwinds every bind this call already made, in
    // reverse. The loop above still runs to completion first so every failing
    // slot is reported in one pass rather than one per attempt.
    if (errors.length > 0) {
        errors.push(...await unwindCommands(commands));
        return { errors, command: null };
    }

    if (commands.length === 0) return { errors, command: null };

    const command = new CompositeCommand(`Adopt ${sceneObject.name} into the theme`, commands);
    if (options.record !== false && !getUndoManager().isUndoRedoing) {
        getUndoManager().push(command);
    }
    // One event, for the one behavior this touched. The second event the
    // previous implementation sent announced the provider's new declarations;
    // adoption no longer writes any.
    if (options.notify !== false) {
        dispatchBehaviorChanged({
            objectPath: sceneObject.getPath(),
            behaviorType: 'Renderer',
            action: 'updated',
        });
    }
    return { errors, command };
}
